import { ACCESS_TOKEN_STORAGE_KEY } from '@/features/auth/constants.ts';
import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import { computed, watch } from 'vue';
import {
  getCurrentCustomer,
  logoutCustomer,
} from '@/api/endpoints/customerEndpoints.ts';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { CARD_UUID_STORAGE_KEY } from '@/features/cart/composables/useCartStore.ts';
import * as Sentry from '@sentry/vue';

export const useAuthStore = defineStore('auth', () => {
  const route = useRoute();
  const router = useRouter();

  // For now, save the token in local storage TODO: In the future, use a more secure method
  const accessToken = useLocalStorage<string | null>(
    ACCESS_TOKEN_STORAGE_KEY,
    null,
  );

  const queryClient = useQueryClient();
  const {
    data: customer,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customer', 'me'],
    queryFn: getCurrentCustomer,
    enabled: computed(() => accessToken.value !== null),
  });

  // Set the user in Sentry
  watch(
    customer,
    (newCustomer) => {
      if (!newCustomer) {
        return;
      }

      Sentry.setUser({
        email: newCustomer.email,
        id: newCustomer.id,
        username: newCustomer.first_name + ' ' + newCustomer.last_name,
      });
    },
    {
      immediate: true,
    },
  );

  // Unset the user in Sentry when the access token is removed
  watch(
    accessToken,
    (newAccessToken) => {
      if (!newAccessToken) {
        Sentry.setUser(null);
      }
    },
    {
      immediate: true,
    },
  );

  watch(error, () => {
    if (
      error.value &&
      isAxiosError(error.value) &&
      error.value.response?.status === 401
    ) {
      queryClient.invalidateQueries();
      queryClient.removeQueries();
      accessToken.value = null;
    }
  });
  const cartUuid = useLocalStorage<string | null>(CARD_UUID_STORAGE_KEY, null);

  return {
    accessToken,
    isAuthenticated: computed(() => accessToken.value !== null),
    logout() {
      // Send a request to the server to logout. We don't really care about the response so we don't need to await it
      logoutCustomer().catch((err) => {
        console.error('Failed to logout', err);
      });
      queryClient.invalidateQueries();
      queryClient.removeQueries();
      accessToken.value = null;
      cartUuid.value = null;
      if (route.meta.authRequired) {
        router.replace('/cart');
      }
    },
    customer,
    customerLoading: isLoading,
    customerError: error,
  };
});
